import { z } from 'zod';
import { email, isoDate, uuid } from './common.js';

/**
 * Email fields shown before "Add another", after payment and from the profile: more than one
 * as an incentive, but a single address is enough to send (decision 0006).
 */
export const INVITE_FIELDS = 3;
/** Upper bound on one submission; the real throttle is the batch rate limit. */
export const MAX_INVITES_PER_BATCH = 20;
/**
 * Length of an invitation's own `?ref=` code. Inviter codes are 8 characters, so the two can
 * never be confused at checkout (decision 0009).
 */
export const INVITE_CODE_LENGTH = 10;
/** Invitation batches allowed per user per hour (PRD §12, decision 0008). */
export const INVITE_BATCHES_PER_HOUR = 10;

/**
 * Raw strings, not `email`: each address is validated server-side so an invalid one
 * gets its own inline reason instead of failing the whole batch.
 */
export const sendInvitesInput = z.object({
  emails: z.array(z.string().trim().max(254)).min(1).max(MAX_INVITES_PER_BATCH),
});
export type SendInvitesInput = z.infer<typeof sendInvitesInput>;

export const inviteResultStatus = z.enum([
  'sent',
  'invalid',
  'self', // the inviter's own email
  'duplicate', // repeated in the same batch
  'already_registered', // has a paid registration for the event
  'already_invited', // first invitation wins, whoever sent it
]);
export type InviteResultStatus = z.infer<typeof inviteResultStatus>;

export const sendInvitesOutput = z.object({
  /** One entry per submitted email, in the submitted order. */
  results: z.array(z.object({ email: z.string(), status: inviteResultStatus })),
});
export type SendInvitesOutput = z.infer<typeof sendInvitesOutput>;

export const inviteStatus = z.enum(['sent', 'registered', 'bounced']);
export type InviteStatus = z.infer<typeof inviteStatus>;

export const referralSummary = z.object({
  code: z.string(),
  shareUrl: z.string().url(),
  /**
   * Prize-draw chances: one per distinct person with a paid order attributed to this
   * participant (link code first, exact email match as fallback). Refunds drop out.
   */
  chances: z.number().int().nonnegative(),
  /** Of those, people who registered through the link without being in `invites`. */
  viaLinkCount: z.number().int().nonnegative(),
  invites: z.array(
    z.object({
      id: uuid,
      email,
      status: inviteStatus,
      /** This invitation's own link (null for invitations made before per-invitation codes). */
      link: z.string().url().nullable(),
      sentAt: isoDate,
      registeredAt: isoDate.nullable(),
    }),
  ),
});
export type ReferralSummary = z.infer<typeof referralSummary>;
